/**
 * wizardConstants.js
 *
 * MRT Wizard constants.
 * Adapted from TH ToolFinder v4 Phase 4.7 with MRT-specific extensions:
 *   - DRILLING_METHOD step (NEW vs TH TF v4 — handles TH/DTH branch)
 *   - Unified 'replace' entry path with replaceTab sub-state
 *     (vs TH TF v4's separate rig / rockDrill paths)
 *
 * @module components/wizard/wizardConstants
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D1)
 *
 * NOTE on file recovery (17 May 2026): the committed copy of this file was
 * truncated at `STEPS.HOLE_DIA` mid-token in HEAD (see
 * `STATUS-2026-05-17-EOD.md`, Rule 3: OneDrive truncation casualty list).
 * Restored during SP3.A.1 so the build can run. STEP_SEQUENCES were
 * reconstructed from the wizard-step components that consume them
 * (StepApplication → StepDrillingMethod → StepEquipmentClass →
 * StepHoleDiameter → StepRockConditions → StepSummary for EXPLORE;
 * StepEntryPoint → StepEquipmentSelect for REPLACE) and from
 * `WizardContext.getProgressConfig` which inserts the conditional sub-
 * steps EQUIPMENT_THREAD_CHOICE / THREAD_CHOICE dynamically.
 */

export const STEPS = Object.freeze({
  ENTRY: 'ENTRY',
  APPLICATION: 'APPLICATION',
  DRILLING_METHOD: 'DRILLING_METHOD',
  EQUIPMENT_CLASS: 'EQUIPMENT_CLASS',
  EQUIPMENT_THREAD_CHOICE: 'EQUIPMENT_THREAD_CHOICE',
  HOLE_DIAMETER: 'HOLE_DIAMETER',
  ROCK_CONDITIONS: 'ROCK_CONDITIONS',
  EQUIPMENT_SELECT: 'EQUIPMENT_SELECT',
  THREAD_CHOICE: 'THREAD_CHOICE',
  SUMMARY: 'SUMMARY',
  RESULTS: 'RESULTS',
});

export const ENTRY_PATHS = Object.freeze({
  EXPLORE: 'explore',
  REPLACE: 'replace',
  DSC: 'dsc',
});

export const INITIAL_SELECTIONS = {
  entryPath: null,
  application: null,
  drillingMethod: null,       // 'TH' | 'DTH' | 'ITH' | 'Rotary' | 'Raiseboring' — explicit drilling method (D3b)
  productLine: null,          // 'TH' | 'DTH' — catalog branch, derived from drillingMethod
  equipmentClass: null,
  threadSystem: null,         // TH path: thread system (e.g. 'ST58', 'CT55') chosen on EQUIPMENT_THREAD_CHOICE
  shankType: null,            // DTH path: shank type (e.g. 'M30', 'DHD340') — DTH parallel of threadSystem
  requiresThreadChoice: false,
  requiresEquipmentThreadChoice: false,
  // D5 REVERT: single mm number when set, null when cleared/skipped.
  // Display unit (mm/inch) is component-local UI state on StepHoleDiameter
  // — the canonical stored value is always mm.
  holeDiameter: null,
  // Replace-path derived constraints (set by equipmentMatcher from the
  // user's rig / rock-drill). Different from `holeDiameter` (the user's
  // pick) — these bound the available diameters shown on the slider.
  holeDiameterMin: null,
  holeDiameterMax: null,
  // Step 5 (D3e). Singular field matching TF v4 StepRockConditions.jsx —
  // see TF v4 line 30 `onSelect('rockCondition', rock.id)`. The earlier
  // MRT nested `rockConditions: { rockHardness, abrasivity, competence,
  // userIndicatedUnknown }` shape was retired 17 May 2026 (port-first).
  rockCondition: null,
  rigManufacturer: null,
  rigModel: null,
  drillManufacturer: null,
  drillModelName: null,
  rockDrillModel: null,
  replaceTab: 'rig',           // 'rig' | 'rockDrill'
};

export const SESSION_STORAGE_KEY = 'mrt-wizard-state';

/**
 * Ordered step sequence for ProgressDots rendering. Path-specific subsets.
 * The conditional sub-steps (EQUIPMENT_THREAD_CHOICE, THREAD_CHOICE) are
 * inserted dynamically by getProgressConfig in WizardContext.
 *
 * EXPLORE path (Path A): APPLICATION → DRILLING_METHOD → EQUIPMENT_CLASS
 *   → [EQUIPMENT_THREAD_CHOICE conditional] → HOLE_DIAMETER → ROCK_CONDITIONS
 *   → SUMMARY → RESULTS.
 *
 * REPLACE path (Path B/C, Sub-Plan 3 D7): EQUIPMENT_SELECT → [THREAD_CHOICE
 *   conditional] → HOLE_DIAMETER → ROCK_CONDITIONS → SUMMARY → RESULTS.
 *
 * DSC path: page-level redirect — no wizard step sequence beyond ENTRY.
 */
export const STEP_SEQUENCES = {
  [ENTRY_PATHS.EXPLORE]: [
    STEPS.APPLICATION,
    STEPS.DRILLING_METHOD,
    STEPS.EQUIPMENT_CLASS,
    STEPS.HOLE_DIAMETER,
    STEPS.ROCK_CONDITIONS,
    STEPS.SUMMARY,
    STEPS.RESULTS,
  ],
  [ENTRY_PATHS.REPLACE]: [
    STEPS.EQUIPMENT_SELECT,
    STEPS.HOLE_DIAMETER,
    STEPS.ROCK_CONDITIONS,
    STEPS.SUMMARY,
    STEPS.RESULTS,
  ],
  [ENTRY_PATHS.DSC]: [],
};
